<script setup lang="ts">
import {computed, onBeforeUnmount, ref} from 'vue';
import ProgramLogo from '@/program/components/program-logo/ProgramLogo.vue';
import {Program} from '@/program/interfaces/Program';
import {Collapsible, CollapsibleContent} from '@/core/components/ui/collapsible';
import {Skeleton} from '@/core/components/ui/skeleton';
import {adacSummary} from '@/amelipro/adacCategories';
import {
    ADAC_GIST_COLOURS,
    AdacCategoryCheck,
    AdacRefusal,
    adacGist,
    adacNeedsNir,
    adacRefusalMessage,
    adacRetryable,
} from '@/amelipro/composables/useAdac';
import NotificationDetectionAdac
    from '@/notification/components/notification-detection/NotificationDetectionAdac.vue';

/**
 * The ADAC programme's card, at the top of the programmes.
 *
 * It shows as soon as ADAC is asked about the patient on screen: on its own
 * for a patient a checked targeting holds — the card is there before the
 * answer — and at a click on the ADAC tab otherwise. A skeleton while ADAC is
 * asked, then the answer in two lines; the whole answer, category by category,
 * unfolds under the card on hover, where the other programmes unfold their
 * feedback.
 */
const props = defineProps<{
    /** The ADAC programme, for its logo and its name. */
    program: Program;
    /** Where the check stands. */
    state: 'idle' | 'running' | 'done' | 'failed';
    /** One entry per category asked for. */
    checks: AdacCategoryCheck[];
    /** The gateway that did the work, when it was not this post. */
    via: string | null;
    /** Why the check came to nothing, when it did. */
    refusal: AdacRefusal | null;
    /** When the answer landed. */
    checkedAt: Date | null;
}>();

const emit = defineEmits<{
    /** The pharmacist asks for another go. */
    (event: 'retry'): void;
    /** The pharmacist typed the NIR neither the detection nor the file gave. */
    (event: 'check-with-nir', nir: string): void;
}>();

/** The time of the answer, as the counter reads a clock. */
const time = computed(() => props.checkedAt?.toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'}) ?? null);

/** The categories that answered. */
/** The answer as a whole: the same reading as the collapsed entry. */
const gist = computed(() => adacGist(props.checks));

/** The gist in a few words, or why there is none. */
const headline = computed(() => (props.state === 'failed' ? adacRefusalMessage(props.refusal) : gist.value.message));

/** The gist's colour; grey when there is no answer to colour. */
const tone = computed(() => (props.state === 'failed' ? 'bg-gray' : ADAC_GIST_COLOURS[gist.value.tone]));

/**
 * What ADAC counted, where it counted anything, and what did not answer:
 * « Subutex 3 × 8 mg · Bandelettes 150 remboursées · Capteurs non communiqué ».
 * The rest of the answer is in the detail.
 */
const counted = computed(() => [
    ...gist.value.reimbursed.map((check) => {
        const summary = adacSummary(check.category, check.verdict!);

        return `${check.category.short} ${summary.count.toLocaleString('fr-FR')}${summary.detail ? ` ${summary.detail}` : ''}`;
    }),
    ...gist.value.unknown.map(check => `${check.category.short} non communiqué`),
].join(' · '));

/** Whether there is anything to unfold: the answer, or what to do about a refusal. */
const hasDetail = computed(() => props.state === 'done'
    || (props.state === 'failed' && (adacNeedsNir(props.refusal) || adacRetryable(props.refusal))));

/** Whether the cursor rests on the card, past the short delay that keeps a passing cursor from unfolding it. */
const hovered = ref(false);

/** Whether the focus is in the detail: a NIR being typed keeps it open when the cursor wanders. */
const focused = ref(false);

/** Whether the detail was opened from the card's button, mouse or keyboard: it stays open until closed there. */
const pinned = ref(false);

/** The detail's id, for the button that controls it. */
const detailId = `adac-detail-${Math.random().toString(36).slice(2, 10)}`;

let hoverTimer: ReturnType<typeof setTimeout> | null = null;

const onHoverStart = (): void => {
    hoverTimer = setTimeout(() => {
        hovered.value = true;
    }, 200);
};

const onHoverEnd = (): void => {
    if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = null;
    }

    hovered.value = false;
};

const detail = ref<HTMLElement | null>(null);

const onFocusOut = (event: FocusEvent): void => {
    focused.value = !!detail.value?.contains(event.relatedTarget as Node | null);
};

onBeforeUnmount(onHoverEnd);

/** Whether the detail is unfolded. */
const open = computed(() => hasDetail.value && (hovered.value || focused.value || pinned.value));
</script>

<template>
  <div
    class="w-full pl-5"
    @mouseenter="onHoverStart"
    @mouseleave="onHoverEnd"
  >
    <div class="relative rounded-md border-2 border-transparent bg-white shadow-custom transition-colors duration-200 hover:border-primary/25">
      <button
        type="button"
        class="flex h-20 w-full items-center rounded-md py-2 pl-9 pr-3 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
        :disabled="!hasDetail"
        :aria-expanded="open"
        :aria-controls="detailId"
        @click="pinned = !pinned"
      >
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline justify-between gap-2">
            <p class="truncate font-semibold text-primary">
              {{ program.name }}
            </p>
            <span
              v-if="time"
              class="shrink-0 text-xs text-gray-dark/60"
            >vérifié {{ time }}</span>
          </div>
          <!-- Idle only for the instant before the automatic check starts: it already waits. -->
          <div
            v-if="state === 'running' || state === 'idle'"
            class="mt-2 space-y-1.5"
            aria-label="Vérification ADAC en cours"
          >
            <Skeleton class="h-2.5 w-3/4" />
            <Skeleton class="h-2.5 w-1/2" />
          </div>
          <template v-else>
            <p class="mt-1 flex items-start gap-1.5 font-semibold text-gray-dark">
              <span
                class="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                :class="tone"
              />
              <span :class="state === 'failed' ? 'line-clamp-2 leading-snug' : 'truncate'">{{ headline }}</span>
            </p>
            <p
              v-if="state === 'done' && counted"
              class="truncate pl-3.5 text-xs text-gray-dark/60"
            >
              {{ counted }}
            </p>
          </template>
        </div>
      </button>
      <ProgramLogo
        :program="program"
        :size="50"
        class="absolute -left-[25px] top-[13px]"
      />
      <Collapsible
        :open="open"
        :unmount-on-hide="false"
      >
        <CollapsibleContent :id="detailId">
          <div
            ref="detail"
            class="border-t border-gray-200/70 px-3 py-2"
            @focusin="focused = true"
            @focusout="onFocusOut"
          >
            <NotificationDetectionAdac
              :state="state"
              :checks="checks"
              :via="via"
              :refusal="refusal"
              @retry="emit('retry')"
              @check-with-nir="(nir) => emit('check-with-nir', nir)"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  </div>
</template>
